import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { Typogaphy } from 'vnetwork-ui'
import {
    FileText,
    Receipt,
    Ticket,
    Briefcase,
    Building2,
    User,
    Mail,
} from 'lucide-react'
import {
    getCustomerActivityLogs,
    ActivityLogItem,
} from '@/modules/Customer/services'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { formatFromUTC } from '@/utils/function'
import DateRangeSelector, { DateRange } from '@/components/DateRangeSelector'
import { Badge } from '@/components/ui/badge'
import {
    DataTableCustomized,
    DataTableColumn,
} from '@/components/ui/data-table-customized'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import Changelogs from '@/components/Activities/Changelogs'

// Strip HTML tags from string
const stripHtml = (html: string): string => {
    if (!html) return ''
    return html.replace(/<[^>]*>/g, '').trim()
}

// Log type config with colors and icons
const LOG_TYPE_CONFIG: Record<
    string,
    { icon: typeof User; color: string; bg: string }
> = {
    customer_log: {
        icon: User,
        color: 'text-blue-600',
        bg: 'bg-blue-50 border-blue-200',
    },
    service_update: {
        icon: FileText,
        color: 'text-emerald-600',
        bg: 'bg-emerald-50 border-emerald-200',
    },
    invoice_update: {
        icon: Receipt,
        color: 'text-amber-600',
        bg: 'bg-amber-50 border-amber-200',
    },
    ticket_log: {
        icon: Ticket,
        color: 'text-rose-600',
        bg: 'bg-rose-50 border-rose-200',
    },
    contract_update: {
        icon: Briefcase,
        color: 'text-violet-600',
        bg: 'bg-violet-50 border-violet-200',
    },
    company_log: {
        icon: Building2,
        color: 'text-slate-600',
        bg: 'bg-slate-50 border-slate-200',
    },
    mailer_log: {
        icon: Mail,
        color: 'text-cyan-600',
        bg: 'bg-cyan-50 border-cyan-200',
    },
}

// Action badge styles
const getActionBadge = (action?: string) => {
    const a = action?.toLowerCase() || ''
    if (a.includes('create') || a.includes('add')) {
        return 'bg-green-50 text-green-600 border-green-200'
    }
    if (a.includes('update') || a.includes('edit')) {
        return 'bg-blue-50 text-blue-600 border-blue-200'
    }
    if (a.includes('delete') || a.includes('remove')) {
        return 'bg-red-50 text-red-600 border-red-200'
    }
    if (a.includes('send') || a.includes('sent')) {
        return 'bg-emerald-50 text-emerald-600 border-emerald-200'
    }
    return 'bg-gray-50 text-gray-600 border-gray-200'
}

const ActivityLogs = () => {
    const { t } = useTranslation()
    const { id } = useParams()
    const customerId = Number(id)

    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(20)
    const [dateRange, setDateRange] = useState<DateRange | null>(null)

    const { data: logsData, isLoading } = useQueryWithHandler({
        queryKey: [
            QUERY_KEYS.CUSTOMER_ACTIVITY_LOGS,
            customerId,
            page,
            pageSize,
            dateRange?.gte,
            dateRange?.lte,
        ],
        queryFn: () =>
            getCustomerActivityLogs({
                customer_id: customerId,
                params: {
                    page,
                    page_size: pageSize,
                    gte: dateRange?.gte,
                    lte: dateRange?.lte,
                },
            }),
        enabled: !!customerId,
    })

    const logs = logsData?.results || []
    const total = logsData?.pagination?.total || 0

    const columns: DataTableColumn<ActivityLogItem>[] = useMemo(
        () => [
            {
                key: 'created_at',
                title: t('activity_logs.columns.time', 'Thời gian'),
                width: 140,
                render: (value) => (
                    <span className="text-xs text-gray-600 whitespace-nowrap">
                        {formatFromUTC(value, 'DD/MM/YYYY HH:mm')}
                    </span>
                ),
            },
            {
                key: 'employee_name',
                title: t('activity_logs.columns.employee', 'Nhân viên'),
                width: 140,
                render: (value) => (
                    <span className="text-xs font-medium text-gray-800">
                        {value || '-'}
                    </span>
                ),
            },
            {
                key: 'log_type',
                title: t('activity_logs.columns.type', 'Loại'),
                width: 130,
                render: (value) => {
                    const config =
                        LOG_TYPE_CONFIG[value] || LOG_TYPE_CONFIG.customer_log
                    const Icon = config.icon
                    return (
                        <span
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-medium ${config.bg}`}
                        >
                            <Icon className={`w-3 h-3 ${config.color}`} />
                            <span className={config.color}>
                                {t(`activity_logs.types.${value}`, {
                                    defaultValue: value?.replace(/_/g, ' '),
                                })}
                            </span>
                        </span>
                    )
                },
            },
            {
                key: 'action_type',
                title: t('activity_logs.columns.action', 'Hành động'),
                width: 180,
                render: (value, row) => {
                    const actionKey = value?.toLowerCase()?.replace(/\s+/g, '_')
                    const combinedKey = `${row.log_type}:${actionKey}`
                    return (
                        <Badge
                            variant="outline"
                            className={`text-[10px] px-1.5 py-0.5 ${getActionBadge(value)}`}
                        >
                            {t(`activity_logs.action_types.${combinedKey}`, {
                                defaultValue: t(
                                    `activity_logs.action_types.${actionKey}`,
                                    { defaultValue: value?.replace(/_/g, ' ') }
                                ),
                            })}
                        </Badge>
                    )
                },
            },
            {
                key: 'description',
                title: t('activity_logs.columns.description', 'Mô tả'),
                width: 280,
                render: (value) => (
                    <span
                        className="text-xs text-gray-600 line-clamp-2"
                        title={stripHtml(value)}
                    >
                        {stripHtml(value) || '-'}
                    </span>
                ),
            },
            {
                key: 'ref_id',
                title: 'Ref ID',
                width: 80,
                render: (value) =>
                    value ? (
                        <span className="text-xs text-primary font-mono">
                            #{value}
                        </span>
                    ) : (
                        <span className="text-xs text-gray-400">-</span>
                    ),
            },
        ],
        [t]
    )

    return (
        <div className="space-y-2">
            {/* Header with filters */}
            <div className="bg-white rounded-md p-4 border border-gray-100 shadow-sm">
                <div className="flex items-center justify-between">
                    <Typogaphy className="text-sm font-semibold text-gray-700">
                        {t('activity_logs.title')}
                    </Typogaphy>
                    <div className="w-64">
                        <DateRangeSelector
                            value={dateRange ?? undefined}
                            onChange={setDateRange}
                        />
                    </div>
                </div>
            </div>

            {/* Table */}
            <DataTableCustomized<ActivityLogItem>
                columns={columns}
                rows={logs}
                size="small"
                loading={isLoading}
                emptyMessage={t('no_data', 'Không có dữ liệu')}
                rowKey={(row) => `${row.log_type}-${row.log_id}`}
                maxHeight="500px"
                pinnedHeader
                expandable={{
                    rowExpandable: (row) =>
                        !!row.metadata &&
                        Object.keys(row.metadata ?? {}).length > 0,
                    expandedRowRender: (row) => (
                        <div className="px-4 py-2">
                            <Changelogs metadata={row.metadata} />
                        </div>
                    ),
                }}
                pagination={{
                    page,
                    pageSize,
                    total,
                    onPageChange: setPage,
                    onPageSizeChange: (size) => {
                        setPageSize(size)
                        setPage(1)
                    },
                    pageSizeOptions: [10, 20, 50],
                }}
            />
        </div>
    )
}

export default ActivityLogs
